/**
 * A callout's arrows. One callout may point at several marks ("SC1/SC2 stations 99+05 and
 * 99+30" over two bands): the first arrow is the callout's own `target`, the others are in
 * `also`. Everything here treats them as one list and keeps documents tidy as marks come and
 * go — an arrow never outlives its mark, a callout never points twice at one mark, and a
 * callout with no arrow left goes with its last mark.
 */
import type { Callout, CalloutTarget, Item, MapDoc } from '../../shared/doc.ts';

/** Every arrow of a callout, its own first. */
export function arrowsOf(c: Callout): CalloutTarget[] {
  const first: CalloutTarget = { target: c.target, targetId: c.targetId };
  if (c.pinned) first.pinned = true;
  if (c.face) first.face = c.face;
  if (c.off) first.off = true;
  return c.also?.length ? [first, ...c.also] : [first];
}

/** The callout pointing with these arrows instead (at least one). */
export function withArrows(c: Callout, arrows: readonly CalloutTarget[]): Callout {
  const [first, ...rest] = arrows;
  if (!first) return c;
  const { pinned: _p, face: _f, off: _o, also: _a, ...bare } = c;
  return { ...bare, ...first, ...(rest.length ? { also: rest } : {}) };
}

export function pointsAt(c: Callout, markId: string): boolean {
  return c.targetId === markId || (c.also?.some((a) => a.targetId === markId) ?? false);
}

/** The ids of the marks a callout points at, in the order of its arrows. */
export function markIdsOf(c: Callout): string[] {
  return arrowsOf(c).flatMap((a) => (a.targetId ? [a.targetId] : []));
}

/** The callouts pointing at a mark. */
export function calloutsAt(items: readonly Item[], markId: string): Callout[] {
  return items.filter((it): it is Callout => it.t === 'callout' && pointsAt(it, markId));
}

/** One more arrow, at a mark the callout does not point at yet. */
export function addArrow(c: Callout, arrow: CalloutTarget): Callout {
  if (arrow.targetId && pointsAt(c, arrow.targetId)) return c;
  return withArrows(c, [...arrowsOf(c), arrow]);
}

/** Without its arrow(s) at a mark; the callout itself when that would leave it none. */
export function removeArrowAt(c: Callout, markId: string): Callout {
  const left = arrowsOf(c).filter((a) => a.targetId !== markId);
  return left.length && pointsAt(c, markId) ? withArrows(c, left) : c;
}

/** One arrow changed (by its place in `arrowsOf`). */
export function updateArrow(c: Callout, index: number, fn: (a: CalloutTarget) => CalloutTarget): Callout {
  const arrows = arrowsOf(c);
  const at = arrows[index];
  if (!at) return c;
  arrows[index] = fn(at);
  return withArrows(c, arrows);
}

/**
 * Marks that are gone take the arrows pointing at them along. A callout that pointed at
 * nothing else goes too (returned in `gone`, to be removed with the marks).
 */
export function withoutMarks(doc: MapDoc, marks: ReadonlySet<string>): { doc: MapDoc; gone: Set<string> } {
  const gone = new Set<string>(marks);
  const items = doc.items.map((it) => {
    if (it.t !== 'callout' || !arrowsOf(it).some((a) => a.targetId && marks.has(a.targetId))) return it;
    const left = arrowsOf(it).filter((a) => !(a.targetId && marks.has(a.targetId)));
    if (!left.length) {
      gone.add(it.id);
      return it;
    }
    return withArrows(it, left);
  });
  return { doc: { ...doc, items: items.filter((it) => !gone.has(it.id)) }, gone };
}

/** Arrows at marks taken into another mark point at that one now — once: a second arrow at the same mark is dropped. */
export function retargetArrows(doc: MapDoc, from: ReadonlySet<string>, to: string): MapDoc {
  return {
    ...doc,
    items: doc.items.map((it) => {
      if (it.t !== 'callout' || !arrowsOf(it).some((a) => a.targetId && from.has(a.targetId))) return it;
      const seen = new Set<string>();
      const arrows: CalloutTarget[] = [];
      for (const a of arrowsOf(it)) {
        const moved = a.targetId && from.has(a.targetId);
        // an arrow carried over aims afresh: where it was put belongs to the mark that is gone
        const next: CalloutTarget = moved ? { target: a.target, targetId: to } : a;
        if (next.targetId) {
          if (seen.has(next.targetId)) continue;
          seen.add(next.targetId);
        }
        arrows.push(next);
      }
      // an arrow that was already at the surviving mark wins over one carried over
      const kept = arrowsOf(it).find((a) => a.targetId === to);
      return withArrows(it, arrows.map((a) => (a.targetId === to && kept ? kept : a)));
    }),
  };
}
